/**
 * CreatePaymentOrderDialog — «Crear enllaç de pagament» (`plans/stripe-payment-links.md` §3.2, F5).
 *
 * A petició i prou (decisió 1): s'obre des del popover, demana la proposta (`preview`) i no
 * escriu res fins que la persona prem «Crear enllaç». Totes les reserves del grup amb caselles
 * (decisió 2), cada import editable (decisió 3; per sobre del pendent, un avís que no bloqueja:
 * en Pere, 14-OCT-2026) i la data màxima per defecte editable (decisió 5). El sistema no envia res a l'hoste: en acabar ensenya l'enllaç per copiar.
 *
 * Els imports són euros a la pantalla i cèntims enters al cable (`lib/payment-orders.ts`).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Check, Copy, Loader2 } from 'lucide-react';
import type { PaymentOrderActionResult, PaymentOrderPreview } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { formatCalendarDate } from '@/lib/dates';
import { useCreatePaymentOrder, usePaymentOrderPreview } from '@/hooks/usePaymentOrders';
import {
  buildCreateOrderBody,
  buildCreateOrderRows,
  buildCreateOrderSchema,
  createOrderDefaults,
  formatCents,
  formatDueAt,
  gatewayOffText,
  orderWarningTexts,
  overOutstandingWarning,
  paymentOrderErrorText,
  proposalErrorText,
  selectedTotalCents,
  type CreateOrderFormValues,
} from '@/lib/payment-orders';
import { PAYMENT_DIALOG_Z, copyPaymentLink, handleCreateOrderError } from './payment-link-utils';

function Warning({ children, tone = 'amber' }: { children: ReactNode; tone?: 'amber' | 'red' }) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        tone === 'red'
          ? 'border-red-200 bg-red-50 text-red-800'
          : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

// ─── El formulari ────────────────────────────────────────────────────────────

export interface CreatePaymentOrderFormProps {
  preview: PaymentOrderPreview;
  pending: boolean;
  onSubmit: (values: CreateOrderFormValues) => void;
  onCancel: () => void;
  /** Per als tests: uns valors de sortida diferents dels de la proposta. */
  initialValues?: CreateOrderFormValues;
  /** Un avís de sobre el formulari (p. ex. «l'import ha canviat: torna a confirmar»). */
  notice?: string | null;
}

/** Caselles, imports, total, data màxima i avisos. Sense xarxa: rep la proposta i torna valors. */
export function CreatePaymentOrderForm({
  preview,
  pending,
  onSubmit,
  onCancel,
  initialValues,
  notice = null,
}: CreatePaymentOrderFormProps) {
  const rows = useMemo(() => buildCreateOrderRows(preview), [preview]);
  const schema = useMemo(() => buildCreateOrderSchema(preview), [preview]);
  const defaults = useMemo(
    () => initialValues ?? createOrderDefaults(preview),
    [initialValues, preview],
  );
  const form = useForm<CreateOrderFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
    mode: 'onChange',
  });
  const lines = useWatch({ control: form.control, name: 'lines' });
  const noDue = useWatch({ control: form.control, name: 'noDue' });
  const { errors } = form.formState;
  const linesError =
    (errors.lines as unknown as { message?: string; root?: { message?: string } } | undefined)?.root
      ?.message ?? (errors.lines as unknown as { message?: string } | undefined)?.message;

  const { gateway, proposal } = preview;
  const canCreate = gateway.ready && proposal !== null;
  const totalCents = selectedTotalCents(lines);
  const anyManualPartial = (proposal?.lines ?? []).some((l) => l.manualPartialUnknown);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-3"
      data-testid="create-payment-order-form"
      noValidate
    >
      {notice && (
        <div data-testid="payment-order-notice">
          <Warning tone="red">{notice}</Warning>
        </div>
      )}
      {!gateway.ready && <Warning tone="red">{gatewayOffText(gateway.offReason)}</Warning>}
      {gateway.ready && gateway.mode === 'test' && (
        <Warning>Mode de PROVA: l'enllaç no cobrarà diners de debò.</Warning>
      )}
      {preview.proposalError && <Warning tone="red">{proposalErrorText(preview.proposalError)}</Warning>}
      {preview.openOrders.length > 0 && (
        <Warning>
          Ja hi ha un enllaç obert ({preview.openOrders.map((o) => formatCents(o.amountCents)).join(', ')}
          ). En crear-ne un de nou, l'anterior deixa de valer.
        </Warning>
      )}
      {preview.policyDependent && (
        <Warning>
          El pendent d'alguna reserva depèn de com es comptin les devolucions (encara per decidir):
          la proposta porta l'import més baix. Revisa'l abans de crear.
        </Warning>
      )}
      {anyManualPartial && (
        <Warning>
          Alguna reserva consta «parcial» marcada a mà i no se sap quant s'ha cobrat: revisa'n
          l'import.
        </Warning>
      )}

      <div className="flex flex-col gap-2" role="group" aria-label="Reserves de l'enllaç">
        {rows.map((row, index) => {
          const { reservation, line } = row;
          const selected = lines?.[index]?.selected ?? false;
          const amountError = errors.lines?.[index]?.amount?.message;
          // Per sobre del pendent: avís en vermell que NO bloqueja (en Pere, 14-OCT-2026).
          const overWarning =
            line && selected && !amountError
              ? overOutstandingWarning(lines?.[index]?.amount, line.outstandingCents)
              : null;
          const inputId = `payment-order-amount-${index}`;
          const title = reservation.roomCode ?? `Reserva ${reservation.beds24Id}`;
          return (
            <div
              key={reservation.reservationId}
              className={cn(
                'flex flex-col gap-1 rounded-md border px-3 py-2',
                line ? 'border-gray-200' : 'border-gray-100 bg-gray-50 text-gray-500',
              )}
              data-testid="payment-order-row"
              data-disabled={line ? undefined : 'true'}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`payment-order-selected-${index}`}
                  checked={selected}
                  disabled={!line || pending}
                  aria-label={`Incloure ${title}`}
                  onCheckedChange={(checked) => {
                    if (!line) return;
                    form.setValue(`lines.${index}.selected`, checked, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    void form.trigger('lines');
                  }}
                />
                <label
                  htmlFor={`payment-order-selected-${index}`}
                  className="flex-1 min-w-0 text-sm"
                >
                  <span className="font-medium">{title}</span>
                  {reservation.isGroupMaster && (
                    <span className="ml-1 rounded bg-blue-50 px-1 text-[10px] font-medium text-blue-700">
                      principal
                    </span>
                  )}
                  <span className="block text-xs text-gray-500">
                    {formatCalendarDate(reservation.checkIn, 'ca', 'd MMM')} –{' '}
                    {formatCalendarDate(reservation.checkOut, 'ca', 'd MMM')} · {reservation.beds24Id}
                    {row.disabledReason ? ` · ${row.disabledReason}` : ''}
                  </span>
                </label>
                {line && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Label htmlFor={inputId} className="sr-only">
                      Import de {title} en euros
                    </Label>
                    <Input
                      id={inputId}
                      inputMode="decimal"
                      autoComplete="off"
                      className={cn('h-8 w-24 text-right', amountError && 'border-red-500')}
                      disabled={!selected || pending}
                      defaultValue={defaults.lines[index]?.amount ?? ''}
                      aria-invalid={amountError ? true : undefined}
                      {...form.register(`lines.${index}.amount`, {
                        onChange: () => void form.trigger('lines'),
                      })}
                    />
                    <span className="text-sm text-gray-600">€</span>
                  </div>
                )}
              </div>
              {line && (
                <p className="pl-6 text-xs text-gray-500">
                  Pendent: {formatCents(line.outstandingCents)} (preu {formatCents(line.priceCents)},
                  cobrat {formatCents(line.paidNetCents)})
                  {line.priceInexact ? ' · preu arrodonit al cèntim' : ''}
                </p>
              )}
              {amountError && selected && <p className="pl-6 text-xs text-red-600">{amountError}</p>}
              {overWarning && (
                <p className="pl-6 text-xs text-red-600" data-testid="payment-order-over-outstanding">
                  {overWarning}
                </p>
              )}
            </div>
          );
        })}
        {linesError && <p className="text-xs text-red-600">{linesError}</p>}
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-sm">
        <span className="font-medium">Total de l'enllaç</span>
        <span className="text-base font-bold" data-testid="payment-order-total">
          {formatCents(totalCents)}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="payment-order-due">Data màxima per pagar</Label>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            id="payment-order-due"
            type="datetime-local"
            className="h-8 w-auto"
            disabled={noDue || pending || !proposal}
            defaultValue={defaults.dueAt}
            {...form.register('dueAt')}
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={noDue}
              disabled={pending || !proposal}
              aria-label="Sense data màxima"
              onCheckedChange={(checked) =>
                form.setValue('noDue', checked, { shouldValidate: true, shouldDirty: true })
              }
            />
            Sense data
          </label>
        </div>
        {proposal?.dueAt && (
          <p className="text-xs text-gray-500">Per defecte: {formatDueAt(proposal.dueAt)}</p>
        )}
        {proposal?.dueAfterCheckIn && !noDue && (
          <p className="text-xs text-amber-700">
            Compte: la data per defecte cau el dia de l'entrada o més tard.
          </p>
        )}
        {errors.dueAt && <p className="text-xs text-red-600">{errors.dueAt.message}</p>}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Tancar
        </Button>
        <Button type="submit" disabled={!canCreate || pending} aria-label="Crear enllaç">
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear enllaç
        </Button>
      </DialogFooter>
    </form>
  );
}

// ─── L'enllaç creat ──────────────────────────────────────────────────────────

export interface PaymentLinkCreatedProps {
  result: PaymentOrderActionResult;
  onCopy: (url: string) => void;
  onClose: () => void;
  copied?: boolean;
}

/** Després de crear: l'enllaç i «Copiar». Enviar-lo és cosa de la persona. */
export function PaymentLinkCreated({ result, onCopy, onClose, copied = false }: PaymentLinkCreatedProps) {
  const { order } = result;
  return (
    <div className="flex flex-col gap-3" data-testid="payment-link-created">
      <p className="text-sm">
        Enllaç creat per <span className="font-semibold">{formatCents(order.amountCents)}</span>
        {order.dueAt ? `, fins al ${formatDueAt(order.dueAt)}` : ', sense data màxima'}.
      </p>
      {order.testMode && (
        <Warning>Enllaç de PROVA: no cobra diners de debò i no s'ha d'enviar a cap hoste.</Warning>
      )}
      {orderWarningTexts(result.warnings).map((warning) => (
        <div key={warning.code} data-testid="payment-order-result-warning" data-warning={warning.code}>
          <Warning tone={warning.urgent ? 'red' : 'amber'}>{warning.text}</Warning>
        </div>
      ))}
      {result.supersededOrderIds.length > 0 && (
        <p className="text-xs text-gray-600">L'enllaç anterior ha deixat de valer.</p>
      )}
      <div className="flex items-center gap-2">
        <Input readOnly value={order.payUrl} aria-label="Enllaç de pagament" className="h-9 flex-1 text-xs" />
        <Button type="button" onClick={() => onCopy(order.payUrl)} aria-label="Copiar enllaç">
          {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          Copiar
        </Button>
      </div>
      <p className="text-xs text-gray-500">
        El sistema no envia res a l'hoste: copia l'enllaç o envia'l per WhatsApp des de la reserva, i
        després marca'l com enviat.
      </p>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Fet
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─── El diàleg ───────────────────────────────────────────────────────────────

export interface CreatePaymentOrderDialogProps {
  reservationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreatePaymentOrderDialog({ reservationId, open, onOpenChange }: CreatePaymentOrderDialogProps) {
  const [result, setResult] = useState<PaymentOrderActionResult | null>(null);
  const [copied, setCopied] = useState(false);
  /** «L'import ha canviat»: es queda sobre la proposta nova fins que es crea o es tanca. */
  const [notice, setNotice] = useState<string | null>(null);
  const preview = usePaymentOrderPreview(reservationId, open && result === null);
  const create = useCreatePaymentOrder();
  const previewData = preview.data;

  const close = () => {
    setResult(null);
    setCopied(false);
    setNotice(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto sm:max-w-lg', PAYMENT_DIALOG_Z)}>
        <DialogHeader>
          <DialogTitle>{result ? 'Enllaç de pagament creat' : 'Crear enllaç de pagament'}</DialogTitle>
          <DialogDescription>
            {result
              ? "Ja el pots enviar a l'hoste."
              : "Tria què es cobra. No s'envia res a l'hoste: en acabar tindràs l'enllaç per copiar."}
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <PaymentLinkCreated
            result={result}
            copied={copied}
            onClose={close}
            onCopy={async (url) => setCopied(await copyPaymentLink(url))}
          />
        ) : preview.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculant el pendent…
          </p>
        ) : preview.error || !previewData ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-red-700" role="alert">
              {preview.error ? paymentOrderErrorText(preview.error) : "No s'ha pogut carregar la proposta."}
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Tancar
              </Button>
              <Button type="button" onClick={() => void preview.refetch()}>
                Tornar-ho a provar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <CreatePaymentOrderForm
            // Una proposta nova (després d'un error, per exemple) torna a omplir el formulari.
            key={preview.dataUpdatedAt}
            preview={previewData}
            pending={create.isPending || preview.isFetching === true}
            notice={notice}
            onCancel={close}
            onSubmit={(values) =>
              create.mutate(buildCreateOrderBody(previewData, values), {
                onSuccess: (created) => {
                  setNotice(null);
                  setResult(created);
                },
                // El total ja no és el que es veia: res s'ha creat. Proposta nova i a confirmar
                // una altra vegada (el formulari es torna a omplir amb els imports d'ara).
                onError: (error) =>
                  handleCreateOrderError(error, { setNotice, refetch: () => void preview.refetch() }),
              })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreatePaymentOrderDialog;
